import { ChangeDetectionStrategy, Component, computed, inject, signal } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { InventoryConfig, Marketplace } from "@front/m19-api-client";
import {
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  MODAL_DATA,
  ModalRef,
} from "@front/m19-ui";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import { ProductViewComponent } from "@m19-board/product-view/product-view.component";
import { ProductTagField } from "./catalog-sections/catalog.component";

@Component({
  selector: "app-custom-field-edition",
  template: ` <div class="flex flex-col items-start justify-between" *transloco="let t">
    @if (modalData.asin) {
      <app-product-view
        [asin]="modalData.asin"
        [marketplace]="modalData.marketplace"
        class="mb-4 w-full rounded-lg border border-solid border-gray-300 p-4"
      ></app-product-view>
    }
    <div class="w-full">
      <IFormField>
        <label iLabel>{{ productTagName() }}</label>
        <input
          iInput
          [(ngModel)]="productTagValue"
          [placeholder]="t('custom-field-edition.enter_value_for_this_custom_field')"
          maxlength="100"
          autofocus
          (keydown.enter)="updateProductTag()"
        />
      </IFormField>
    </div>
    <div class="mt-4 flex w-full justify-end gap-2">
      <button iButton variant="destructive" size="sm" (click)="deleteProductTag()">{{ t("common.delete") }}</button>
      <button iButton size="sm" (click)="updateProductTag()">{{ t("common.update") }}</button>
    </div>
  </div>`,

  imports: [
    TranslocoDirective,
    ProductViewComponent,
    IButtonDirective,
    IInputDirective,
    FormsModule,
    IFormFieldComponent,
    ILabelComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CustomFieldEditionComponent {
  readonly translocoService = inject(TranslocoService);

  modalData = inject(MODAL_DATA) as CustomFieldEditionModalData;
  modalRef = inject(ModalRef);

  readonly inventoryConfig = signal(this.modalData.inventoryConfig);
  readonly productTagName = computed(() => this.getProductTagName(this.modalData.productTag));

  productTagValue = signal(this.modalData.value ?? "");

  updateProductTag() {
    this.modalRef.close(this.productTagValue());
  }

  deleteProductTag() {
    this.modalRef.close("");
  }

  private getProductTagName(field: ProductTagField) {
    if (field === ProductTagField.FIELD1) {
      return (
        this.inventoryConfig()?.customField1Name ??
        this.translocoService.translate("custom-field-edition.custom_field_1")
      );
    } else {
      return (
        this.inventoryConfig()?.customField2Name ??
        this.translocoService.translate("custom-field-edition.custom_field_2")
      );
    }
  }
}

export interface CustomFieldEditionModalData {
  asin?: string;
  marketplace: Marketplace;
  productTag: ProductTagField;
  value?: string;
  inventoryConfig?: InventoryConfig;
}

export type CustomFieldEditionOutput = string;
